import i18next from 'i18next';
import { RoleEnum, VehcileAvailabilityEnum, VehicleStateEnum } from 'src/api';
import { EMPTY_FILTER_STATE, RoleScope, VehicleFilterState } from './params';

export interface FilterOption {
	id: string;
	value: string;
	label: string;
}

const STATE_LABELS: Record<string, string> = {
	[VehicleStateEnum.DISPONIBILE]: 'available',
	[VehicleStateEnum.OPZIONATO]: 'optioned',
	[VehicleStateEnum.OSCURATO]: 'obscured',
	[VehicleStateEnum.VENDUTO]: 'sold',
};

const AVAILABILITY_LABELS: Record<string, string> = {
	[VehcileAvailabilityEnum.TARGATO]: 'registered',
	[VehcileAvailabilityEnum.IN_ARRIVO]: 'incoming',
	[VehcileAvailabilityEnum.TELAIATO]: 'framed',
	[VehcileAvailabilityEnum.ORDINATO]: 'ordered',
};

const optionsFrom = (
	id: string,
	labels: Record<string, string>,
	values: string[]
): FilterOption[] =>
	values.map((value) => ({
		id,
		value,
		label: i18next.t(labels[value]).toUpperCase(),
	}));

export const stateOptions = (scope: RoleScope): FilterOption[] => {
	const values = Object.keys(STATE_LABELS).filter(
		(state) => !scope.statoCorrente || scope.statoCorrente.includes(state)
	);

	return optionsFrom('stato_corrente', STATE_LABELS, values);
};

export const availabilityOptions = (): FilterOption[] =>
	optionsFrom('stato_disp', AVAILABILITY_LABELS, Object.keys(AVAILABILITY_LABELS));

interface AuthFlags {
	userRole?: string;
	isSalesFacile?: boolean;
	isSalesStellantis?: boolean;
}

const SELLABLE = [VehicleStateEnum.DISPONIBILE, VehicleStateEnum.OPZIONATO];

export const roleScopeOf = ({
	userRole,
	isSalesFacile,
	isSalesStellantis,
}: AuthFlags): RoleScope => {
	if (userRole === RoleEnum.FLEET_MANAGER) {
		return { statoCorrente: [VehicleStateEnum.DISPONIBILE] };
	}

	if (userRole === RoleEnum.SEGNALATORE) {
		return { quotable: true };
	}

	if (isSalesFacile) {
		return { quotable: true, statoCorrente: SELLABLE };
	}

	if (isSalesStellantis) {
		return { statoCorrente: SELLABLE };
	}

	return {};
};

export const canFilterByUser = (userRole?: string) =>
	userRole === RoleEnum.MASTER || userRole === RoleEnum.BACKOFFICE;

/**
 * Porta nel formato della lista paginata uno stato di filtri che arriva da
 * fuori: sessionStorage scritto dalle vecchie multiselect, o un preset parziale
 * passato con la navigazione.
 */
export const sanitizeStoredFilters = (
	stored: Record<string, any> | null | undefined,
	scope: RoleScope,
	userRole?: string
): VehicleFilterState => {
	const source = stored ?? {};
	const result = { ...EMPTY_FILTER_STATE };
	const target = result as Record<string, unknown>;

	(Object.keys(EMPTY_FILTER_STATE) as (keyof VehicleFilterState)[]).forEach(
		(key) => {
			const value = source[key];
			if (value === undefined || value === null) return;

			const fallback = EMPTY_FILTER_STATE[key];

			if (Array.isArray(fallback)) {
				if (Array.isArray(value)) target[key] = value;
				return;
			}

			if (typeof fallback === 'object') {
				if (typeof value === 'object' && !Array.isArray(value)) {
					target[key] = { from: value.from ?? '', to: value.to ?? '' };
				}
				return;
			}

			target[key] = Array.isArray(value)
				? String(value[0] ?? '')
				: String(value);
		}
	);

	if (scope.statoCorrente) {
		result.stato_corrente = result.stato_corrente.filter((state) =>
			scope.statoCorrente!.includes(state)
		);
	}

	if (!canFilterByUser(userRole)) {
		result.utente = '';
	}

	return result;
};
